import type {ReactElement} from 'react';
import React, {useEffect, useMemo, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import styles from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/play-button/play-button.module.scss';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {SeekbarDurationOptionsContent} from '@Components/poster-editor/components/poster-seekbar/components/seekbar-duration-options-content';
import {AnimatePresence, motion} from 'framer-motion';
import {getCurrentPageDuration, getNumberOfItemsInCurrentPage} from '@Components/poster-editor/poster-editor-reducer';
import {getSelectorTypeOnOpen} from '@Components/poster-editor/components/poster-seekbar/poster-seekbar.helper';
import {SelectorType} from '@Components/poster-editor/components/poster-seekbar/components/change-duration-selector/change-duration-selector.types';
import {useAppSelector} from '@/hooks';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

export function DurationSelector(): ReactElement<any> | null {
  const isExpanded = useAppSelector((state) => {
    return state.posterEditor.isBottomWebSeekbarExpanded;
  });
  const currentPageDuration = useAppSelector(getCurrentPageDuration);

  const getNumOfItems = useMemo(() => {
    return getNumberOfItemsInCurrentPage;
  }, []);
  const numOfItems = useAppSelector((state) => {
    return getNumOfItems(state);
  });
  const [selectorType, setSelectorType] = useState<SelectorType>();

  useEffect((): void => {
    setSelectorType(getSelectorTypeOnOpen());
  }, [numOfItems, currentPageDuration]);

  const getTextForSelector = (): string => {
    switch (selectorType) {
      case SelectorType.CUSTOM:
        return window.i18next.t('pmwjs_custom');
      case SelectorType.AUDIO:
        return window.i18next.t('pmwjs_audio');
      case SelectorType.VIDEO:
        return window.i18next.t('pmwjs_video');
      case SelectorType.SLIDESHOW:
        return window.i18next.t('pmwjs_slideshow');
      default:
        return '';
    }
  };

  const getSelectorForDropdown = (): ReactElement<any> => {
    return <DropdownGhostSelectBox className="spacing-m-l-2" text={getTextForSelector()} size={DROPDOWN_SIZE.XSMALL} />;
  };

  const getCustomDurationPopup = (): ReactElement<any> => {
    return (
      <div className="flex-v-row">
        <SeekbarDurationOptionsContent
          onChangeSelectorTypeCallback={(selectType): void => {
            trackPosterBuilderGA4Events(GA4EventName.EDITOR_DURATION_SELECTOR_CLICKED, {[GA4EventParamName.TYPE]: selectType});
            setSelectorType(selectType);
          }}
        />
      </div>
    );
  };

  const getDropdownForDuration = (): ReactElement<any> => {
    return (
      <Dropdown
        alignment={ALIGNMENT_TYPE.TOP_START}
        position={DROPDOWN_POSITION.FIXED}
        popUpHasCustomWidth
        popUpHasCustomHeight
        selector={getSelectorForDropdown()}
        popup={getCustomDurationPopup()}
        doCloseOnInnerClick={false}
        popupClassName={styles.popup}
        popupOffset={8}
      />
    );
  };

  const getContent = (): ReactElement<any> => {
    return (
      <AnimatePresence initial>
        {isExpanded ? (
          <motion.div initial={{opacity: 0}} animate={{opacity: 1}} transition={{duration: 0.3}} exit={{opacity: 0}}>
            {getDropdownForDuration()}
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  };

  return getContent();
}
